import { useMutation, useQuery } from '@tanstack/react-query';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { IBulkUploadStatus, isTerminalStatus } from '../../schemas/SourcingBulkUpload.schema';
import { getBulkUploadStatus, getBulkUploadTemplates } from '../queries/SourcingBulkUploadQueries';

export const SOURCING_BULK_UPLOAD_STATUS_KEY = 'sourcing-bulk-upload-status';

const POLL_INTERVAL_MS = 2000;

export function useBulkUploadTemplatesMutation() {
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ listId, entityType }: { listId: string; entityType: 'company' | 'person' }) =>
      getBulkUploadTemplates(listId, entityType),
    onError: () => {
      pushErrorToast({ message: 'Failed to open bulk upload. Please try again.' });
    },
  });
}

export function useBulkUploadStatusQuery(id: string | undefined) {
  const { pushErrorToast } = useToastMessageState();

  return useQuery<IBulkUploadStatus>({
    queryKey: [SOURCING_BULK_UPLOAD_STATUS_KEY, id],
    queryFn: async () => {
      try {
        return await getBulkUploadStatus(id!);
      } catch (error) {
        pushErrorToast({ message: 'Failed to fetch upload progress. Please try again.' });
        throw error;
      }
    },
    enabled: Boolean(id),
    // Keep polling until we *confirm* a terminal status. If `data` is still
    // undefined (e.g. the first fetch errored), retry on the next interval so
    // the dialog recovers automatically when the backend comes back.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && isTerminalStatus(data.status)) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });
}
